import { AlkLogger } from '@alk/core';
import { EServerSentEventsClient } from '../../enums';
import { parseStringEvent, parseStringEventToObject } from '../../helpers';
/**
 * SSEClient implementation with custom headers
 * inspired by https://github.com/julienmachon/sse-client/blob/master/src/index.ts
 * */
export class ServerSentEventsClientService {
    constructor() {
        this.CLOSED = EServerSentEventsClient.CLOSED;
        this.CONNECTING = EServerSentEventsClient.CONNECTING;
        this.OPEN = EServerSentEventsClient.OPEN;
        this.controller = new AbortController();
        this.listeners = {};
        this.processMessage = (reader, incompleteMessage) => ({ done, value }) => {
            var _a;
            if (done) {
                this.onClosed();
                return;
            }
            parseStringEvent(value, incompleteMessage)
                .filter(Boolean)
                .map(parseStringEventToObject)
                .forEach(message => {
                this.onMessage(message);
            });
            return (_a = reader.read) === null || _a === void 0 ? void 0 : _a.call(reader).then(this.processMessage(reader, incompleteMessage));
        };
    }
    init(url, init = { headers: new Headers() }) {
        if (this.readyState !== undefined) {
            return;
        }
        const signal = this.controller.signal;
        this.readyState = this.CONNECTING;
        const { headers, ...rest } = init;
        const sseHeaders = new Headers({ 'Content-Type': 'text/event-stream', 'Cache-Control': 'no-store' });
        headers.forEach((value, key) => {
            sseHeaders.append(key, value);
        });
        fetch(url, { signal, ...rest, headers: sseHeaders })
            .then((response) => {
            AlkLogger.info(response.statusText);
            if (!response.ok) {
                this.onError(new Error(response.statusText));
                return;
            }
            if (!Boolean(response.body)) {
                this.onError(new Error('No body'));
                return;
            }
            this.reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
            void this.start(this.reader);
        })
            .catch(error => {
            this.onError(error);
            throw error;
        });
    }
    reset() {
        this.readyState = undefined;
        this.controller = new AbortController();
    }
    addEventListener(eventType, callback) {
        var _a;
        this.listeners[eventType] = [...((_a = this.listeners[eventType]) !== null && _a !== void 0 ? _a : []), callback];
    }
    close() {
        var _a;
        if (this.readyState === this.CONNECTING || this.readyState === this.OPEN) {
            void ((_a = this.reader) === null || _a === void 0 ? void 0 : _a.cancel());
            this.controller.abort();
            this.onClosed();
        }
    }
    removeEventListener(eventType, callback) {
        var _a;
        this.listeners[eventType] = [...((_a = this.listeners[eventType]) !== null && _a !== void 0 ? _a : []).filter(cb => callback !== cb)];
    }
    getReadState() {
        return this.readyState;
    }
    onOpen() {
        var _a;
        this.readyState = this.OPEN;
        (_a = this.onopen) === null || _a === void 0 ? void 0 : _a.call(this, new Event('open'));
    }
    onError(error) {
        var _a;
        this.readyState = this.CLOSED;
        (_a = this.onerror) === null || _a === void 0 ? void 0 : _a.call(this, error);
        this.reset();
    }
    onClosed() {
        var _a;
        this.readyState = this.CLOSED;
        (_a = this.onclose) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    onMessage(objectEvent) {
        var _a, _b;
        if (Boolean(objectEvent.id)) {
            this.lastEventID = objectEvent.id;
        }
        const messageEvent = new MessageEvent(objectEvent.event, {
            data: objectEvent.data,
            lastEventId: this.lastEventID,
        });
        (_a = this.onmessage) === null || _a === void 0 ? void 0 : _a.call(this, messageEvent);
        (_b = this.listeners[objectEvent.event]) === null || _b === void 0 ? void 0 : _b.forEach(callback => callback(messageEvent));
    }
    async start(reader) {
        this.onOpen();
        await reader.read().then(this.processMessage(reader, { message: '' }));
        reader.releaseLock();
    }
}
